import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import AdminDashboard from "../AdminDashboard/admindash";
import {
  Clock,
  Briefcase,
  DollarSign,
  ImageIcon,
  Plus,
  Trash2,
  Eye,
  Loader2,
  Navigation,
  Plane,
  FileText,
  Save,
  ArrowLeft
} from "lucide-react";
import { toast } from "react-toastify";
import API_URL from "../../config";


function UpdateTravelPackage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPackage = async () => {
      try {
        const res = await axios.get(`${API_URL}/travelpackage/${id}`);
        const data = res.data.travelPackage;

        if (!data) return;

        setFormData({
          packageName: data.packageName ?? "",
          packageDescription: data.packageDescription ?? "",
          packageType: data.packageType ?? "",
          destinations: Array.isArray(data.destinations) ? data.destinations.join(", ") : "",
          duration: data.duration ?? "",
          startDate: data.startDate ? data.startDate.slice(0, 10) : "",
          endDate: data.endDate ? data.endDate.slice(0, 10) : "",
          itinerary: Array.isArray(data.itinerary) ? data.itinerary.join(", ") : "",
          inclusions: Array.isArray(data.inclusions) ? data.inclusions.join(", ") : "",
          exclusions: Array.isArray(data.exclusions) ? data.exclusions.join(", ") : "",
          availability: data.availability ?? true,
          currency: data.currency ?? "LKR",
          price: data.price ?? "",
          transportType: data.transportType ?? "",
          travelImages: Array.isArray(data.travelImages) && data.travelImages.length > 0 ? data.travelImages : [""],
        });
      } catch (err) {
        console.error("Error fetching package:", err);
        toast.error("Failed to load travel package data.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchPackage();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleImageChange = (index, value) => {
    const updated = [...formData.travelImages];
    updated[index] = value;
    setFormData({ ...formData, travelImages: updated });
  };

  const addImageField = () => {
    setFormData({ ...formData, travelImages: [...formData.travelImages, ""] });
  };

  const removeImageField = (index) => {
    const updated = formData.travelImages.filter((_, i) => i !== index);
    setFormData({ ...formData, travelImages: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        destinations: formData.destinations ? formData.destinations.split(",").map((s) => s.trim()).filter(s => s !== "") : [],
        itinerary: formData.itinerary ? formData.itinerary.split(",").map((s) => s.trim()).filter(s => s !== "") : [],
        inclusions: formData.inclusions ? formData.inclusions.split(",").map((s) => s.trim()).filter(s => s !== "") : [],
        exclusions: formData.exclusions ? formData.exclusions.split(",").map((s) => s.trim()).filter(s => s !== "") : [],
        travelImages: formData.travelImages.filter((url) => url.trim() !== ""),
      };

      await axios.put(`${API_URL}/travelpackage/${id}`, payload);
      toast.success("Travel Package updated successfully!");
      setTimeout(() => navigate("/travelpackages"), 1500);
    } catch (err) {
      console.error("Error updating package:", err);
      toast.error(err.response?.data?.error || "Update failed.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminDashboard>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <Loader2 className="text-sky-500 animate-spin" size={48} />
          <p className="text-sky-900 font-black uppercase tracking-widest text-xs">Fetching Package Assets...</p>
        </div>
      </AdminDashboard>
    );
  }

  return (
    <AdminDashboard>
      <div className="font-sans">
        {/* Header Section */}
        <div className="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => navigate("/travelpackages")}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-sky-600"
              >
                <ArrowLeft size={18} />
              </button>
              <h1 className="text-2xl font-black text-sky-950 tracking-tight flex items-center gap-2">
                <Briefcase className="text-sky-500" size={24} />
                Refine Journey
              </h1>
            </div>
            <p className="text-sm text-gray-400 font-bold ml-10">Updating details for the curate TravelAura adventure.</p>
          </div>
          <button
            onClick={() => navigate("/travelpackages")}
            className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-sky-600 transition-colors"
          >
            Cancel Edit
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">

          {/* Main Form Section */}
          <div className="lg:col-span-8">
            <form onSubmit={handleSubmit} className="space-y-10">

              {/* Section 1: Package Identity */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-sky-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Package Identity</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Package Name</label>
                    <div className="relative">
                      <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <input
                        name="packageName"
                        value={formData.packageName}
                        onChange={handleChange}
                        placeholder="Package Name"
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Journey Type</label>
                    <div className="relative">
                      <Plane className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={18} />
                      <select
                        name="packageType"
                        value={formData.packageType}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none appearance-none text-sm"
                      >
                        <option value="Adventure">Adventure</option>
                        <option value="Leisure">Leisure</option>
                        <option value="Cultural">Cultural</option>
                        <option value="Wellness">Wellness</option>
                        <option value="Wildlife">Wildlife</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Journey Narrative</label>
                  <div className="relative">
                    <FileText className="absolute left-4 top-4 text-sky-400" size={18} />
                    <textarea
                      name="packageDescription"
                      value={formData.packageDescription}
                      onChange={handleChange}
                      placeholder="Describe the journey..."
                      required
                      rows="3"
                      className="w-full pl-12 pr-4 py-4 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Section 2: Timeline & Logistics */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-indigo-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Journey Logistics</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Duration</label>
                    <div className="relative">
                      <Clock className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
                      <input
                        name="duration"
                        value={formData.duration}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Transport</label>
                    <div className="relative">
                      <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-400" size={18} />
                      <input
                        name="transportType"
                        value={formData.transportType}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Departure Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Return Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-xs"
                    />
                  </div>
                </div>
              </div>

              {/* Section 3: Fine Details */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-amber-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Experience Details</h3>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Destinations (Comma Sep)</label>
                    <input
                      name="destinations"
                      value={formData.destinations}
                      onChange={handleChange}
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Itinerary Highlights (Comma Sep)</label>
                    <textarea
                      name="itinerary"
                      value={formData.itinerary}
                      onChange={handleChange}
                      rows="2"
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none resize-none text-sm"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Section 4: Pricing & Assets */}
              <div className="space-y-6">
                <div className="flex items-center gap-2 border-l-4 border-emerald-500 pl-4 py-1">
                  <h3 className="text-xs font-black text-sky-950 uppercase tracking-widest">Financials & Media</h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Base Price</label>
                    <div className="relative">
                      <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-500" size={18} />
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                        className="w-full pl-12 pr-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Currency</label>
                    <input
                      name="currency"
                      value={formData.currency}
                      onChange={handleChange}
                      placeholder="LKR"
                      className="w-full px-4 py-3.5 bg-gray-50/50 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-sm"
                    />
                  </div>
                  <div className="flex flex-col justify-end pb-1.5">
                    <label className="flex items-center gap-3 bg-emerald-50/50 p-3.5 rounded-xl border border-emerald-100 cursor-pointer hover:bg-emerald-50 transition-all">
                      <input
                        type="checkbox"
                        name="availability"
                        checked={formData.availability}
                        onChange={handleChange}
                        className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className="text-[10px] font-black text-emerald-900 uppercase tracking-widest">Active Status</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Experience Gallery</label>
                    <button
                      type="button"
                      onClick={addImageField}
                      className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-sky-600 hover:text-sky-700 transition-colors"
                    >
                      <Plus size={14} /> Add Image
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.travelImages.map((img, index) => (
                      <div key={index} className="flex gap-3">
                        <div className="relative flex-1">
                          <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-sky-400" size={16} />
                          <input
                            type="text"
                            placeholder="Image URL"
                            value={img}
                            onChange={(e) => handleImageChange(index, e.target.value)}
                            className="w-full pl-11 pr-4 py-3 bg-gray-50/30 border border-gray-100 rounded-xl focus:bg-white transition-all font-bold text-sky-950 outline-none text-xs"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImageField(index)}
                          disabled={formData.travelImages.length === 1}
                          className="p-3 text-rose-300 hover:text-rose-500 transition-all disabled:opacity-0"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="pt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-5 bg-sky-600 text-white font-black rounded-2xl hover:bg-sky-700 active:scale-[0.98] transition-all shadow-xl shadow-sky-600/20 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed uppercase tracking-widest text-sm"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={20} className="animate-spin" />
                      Synchronizing Asset...
                    </>
                  ) : (
                    <>
                      Commit Package Updates
                      <Save size={20} />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Preview Section */}
          <div className="lg:col-span-4 lg:sticky lg:top-8 self-start">
            <div className="flex items-center gap-2 mb-4 ml-2">
              <Eye size={16} className="text-sky-500" />
              <span className="text-[10px] font-black text-sky-950 uppercase tracking-widest">Live Experience Snapshot</span>
            </div>

            <div className="bg-gray-50 border border-gray-100 rounded-[2.5rem] overflow-hidden group border-dashed p-2">
              <div className="bg-white rounded-[2rem] overflow-hidden shadow-sm border border-gray-50">
                <div className="relative h-48 bg-gray-100 overflow-hidden">
                  {formData.travelImages[0] ? (
                    <img
                      src={formData.travelImages[0]}
                      alt="Preview"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000"
                      onError={(e) => {
                        e.target.src = "https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?q=80&w=1000&auto=format&fit=crop";
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-gray-300 gap-2">
                      <ImageIcon size={32} />
                      <p className="text-[9px] font-black uppercase tracking-widest">Visual Feedback</p>
                    </div>
                  )}
                  {formData.packageType && (
                    <div className="absolute top-4 left-4 px-3 py-1.5 bg-white/95 backdrop-blur rounded-full text-[8px] font-black uppercase tracking-widest text-sky-600 shadow-xl shadow-black/5">
                      {formData.packageType}
                    </div>
                  )}
                  <div className="absolute bottom-4 right-4 bg-emerald-500 text-white text-[8px] font-black px-2 py-1 rounded-md shadow-lg shadow-emerald-500/20">
                    EDIT MODE
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-2 text-indigo-500 mb-2">
                    <Clock size={10} />
                    <span className="text-[8px] font-black uppercase tracking-widest">
                      {formData.duration || "Duration TBD"}
                    </span>
                  </div>
                  <h2 className="text-lg font-black text-sky-950 mb-3 truncate leading-tight">
                    {formData.packageName || "Package Asset"}
                  </h2>

                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-xs font-black text-emerald-600">{formData.currency || "LKR"}</span>
                    <span className="text-xl font-black text-sky-950">{formData.price || "0"}</span>
                    <span className="text-[10px] text-gray-400 font-bold">/ person</span>
                  </div>

                  <p className="text-gray-400 text-[10px] font-bold leading-relaxed line-clamp-2 h-10">
                    {formData.packageDescription || "Updating description..."}
                  </p>

                  <div className="mt-6 pt-6 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="text-[7px] font-black text-gray-300 uppercase tracking-widest">Main Hub</span>
                      <span className="text-[9px] font-black text-sky-950">{formData.destinations.split(",")[0] || "TBD"}</span>
                    </div>
                    {formData.transportType && (
                      <div className="px-2 py-1 bg-sky-50 rounded text-[7px] font-black text-sky-600 uppercase">
                        {formData.transportType}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-amber-50 rounded-3xl p-6 border border-amber-100 shadow-sm relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-16 h-16 bg-amber-100/50 rounded-bl-[3rem] -mr-4 -mt-4 transition-all group-hover:scale-110"></div>
              <p className="text-[10px] font-bold text-amber-800 leading-relaxed italic relative z-10">
                "Updated packages with fresh imagery and clear itinerary highlights consistently see higher conversion rates."
              </p>
            </div>
          </div>

        </div>
      </div>
    </AdminDashboard>
  );
}

export default UpdateTravelPackage;
